import { Data, Match } from "effect";
import type { AuditEvent } from "./audit.ts";
import type { AnnotationId } from "./ids.ts";

// Operational sentinels for a manual annotation's `rule`, not pedagogy, so they live in code
// (CLAUDE.md heuristic). Historical: `MANUAL_UNCLASSIFIED` marked a manual annotation awaiting the
// classify pass; `MANUAL_RULE_GAP` marked one the classify pass could not pin to any rule. The
// classify pass (and the rubric-refinement loop it fed) is retired (ADR-0044) — a manual
// (Expert-authored) annotation is now an ordinary open finding, never classified (CONTEXT.md).
// Neither historical constant is written by any *new* machinery in this workspace, but both remain
// exported: existing documents and append-only audit logs already carry these `manual:*` rule
// strings, and every reader (parsers, folds, projections, the web review-sidebar's rule bucketing)
// must keep recognising them.
export const MANUAL_UNCLASSIFIED = "manual:unclassified";
export const MANUAL_RULE_GAP = "manual:rule-gap";

// The canonical `rule` value the server stamps on every *new* manual annotation (ADR-0044, moved
// here from `apps/server/src/rest.ts`'s local constant so the one write-time sentinel lives
// alongside its historical read-only siblings above). It carries no routing meaning of its own — a
// manual finding's identity is `author === "expert"` (`isManualAnnotation`), never its `rule`
// string — it exists only to satisfy `isManualSentinel`'s `manual:` prefix, which
// `write-annotation`'s catalog check exempts, so the Expert's free-text finding never has to cite a
// real rubric rule id.
export const MANUAL_FINDING_RULE = "manual:finding";

// A `manual:*` sentinel is an operational marker, not a rubric citation, so it is exempt from
// catalog validation (it has no catalog rule to match). Keyed on the shared prefix so both
// sentinels above — and any future `manual:*` — are covered. Mirrors the pass-through that
// `canonicalizeRuleId` already gives the same prefix.
export const isManualSentinel = (rule: string): boolean => rule.startsWith("manual:");

// "Is this manual?" is `author === "expert"` (CONTEXT.md) — never inferred from the rule string.
// A manual annotation carries no classification and is never reclassified (the classify pass is
// retired), so this predicate is the sole authority on manual-vs-judge identity.
export const isManualAnnotation = (event: AuditEvent): boolean =>
  Match.value(event).pipe(
    Match.tag("annotation.created", (e) => e.author === "expert"),
    Match.tag("annotation.created.v2", (e) => e.author === "expert"),
    Match.orElse(() => false),
  );

// U7 (CON-739): whether an `annotation.created.v2` is the CHECK SUITE's own per-span write — the one
// producer of that event U7 retired. Such a create is a finding nothing can ever retract: it is
// derived state, its live copy now lives in the `JudgeFindingStore`, and the reconciliation
// that used to retire a stale one is gone. So every openness fold skips it (`projectOpenFindings`,
// `applyEvent`, `projectDiagnostics`) and the store is the sole source of judge findings.
//
// The discriminator is `checkId`, NOT the author role, and the schema was designed for exactly this
// (see `AnnotationCreatedStructured.checkId`: "Stamped only by the check-suite write path"; optional
// and never defaulted precisely so "not produced by the check suite" stays distinguishable). Author
// role would be wrong twice over: `author` DEFAULTS to `unit-judge` at the schema level, so an
// author-based filter silently swallows rows that never claimed to be judge findings, and — the real
// breakage — the agent's own `annotate`/`ln` tool is narrowed to `JudgeAuthor` by `AnnotateArgs`, so
// filtering on the role would retire the agent's hand-authored findings along with the suite's. Those
// stay durable and keep surfacing: nothing regenerates them, which is exactly why they belong in the
// log (the agent's own instructions promise they "persist across sessions as residual findings").
//
// A pre-U3 (CON-734) judge finding carries no `checkId` and so survives this filter — correctly: the
// suite that produced it cannot regenerate it under an id the store would match, so if the log is all
// that remembers it, the log is where it stays open.
export const isCheckSuiteFinding = (event: AuditEvent): boolean =>
  Match.value(event).pipe(
    Match.tag("annotation.created.v2", (e) => e.checkId !== undefined),
    Match.orElse(() => false),
  );

// `strip` targets only an Expert's own manual annotation: the target must have an Expert-authored
// `created` event. A judge annotation (whose fate is a human gate) or an id with no `created` event
// is not a valid strip target. Distinct from `AlreadyDecided` (`pending.ts`), which guards a manual
// finding that is a valid strip target but is no longer open (already kept/rejected/closed).
export class NotManual extends Data.TaggedError("NotManual")<{
  readonly annotationId: AnnotationId;
}> {}

// Whether the id has an Expert-authored `created` event in the log — the precondition for
// `strip-annotation`. False for judge annotations and for ids the log has never seen created.
export const isManualTarget = (events: readonly AuditEvent[], id: AnnotationId): boolean =>
  events.some((e) =>
    Match.value(e).pipe(
      Match.tag(
        "annotation.created",
        (created) => created.annotationId === id && created.author === "expert",
      ),
      Match.tag(
        "annotation.created.v2",
        (created) => created.annotationId === id && created.author === "expert",
      ),
      Match.orElse(() => false),
    ),
  );
